/**
 * Phase 2.15 — Technical Validation Results PDF section.
 *
 * Polished styling: navy header with gold accent strip, generous row
 * padding, alt-row shading, rows expand vertically to fit content, and
 * cross-page row continuation. Promotion column removed (internal-only).
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import {
  buildTvfNarrative,
  summarizeTvf,
  type TvfRow,
} from "@/reports/telecom/technicalValidation";

interface Ctx {
  clientOrgName?: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  startPageNumber?: number;
  sectionNumber?: string;
}

const SEVERITY_COLORS: Record<string, RGB> = {
  Critical:      [153, 27, 27],
  High:          [220, 38, 38],
  Medium:        [245, 158, 11],
  Low:           [148, 163, 184],
  Informational: [100, 116, 139],
};

export function renderTechnicalValidation(
  doc: jsPDF,
  rows: TvfRow[],
  ctx?: Ctx,
): number {
  if (!rows || rows.length === 0) return ctx?.startPageNumber ?? doc.getNumberOfPages();

  doc.addPage();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const bottomLimit = pageH - 70;
  const innerW = pageW - PAGE_MARGIN * 2;
  const sectionNumber = ctx?.sectionNumber ?? "07";

  const drawChrome = (continued: boolean): number => {
    drawRunningHeader(doc, {
      clientName: ctx?.clientOrgName || "Client",
      frameworks: ctx?.frameworks ?? null,
      methodology: ctx?.methodology ?? null,
    });
    return drawSectionTitle(doc, {
      sectionNumber,
      title: "Technical Validation Results",
      continued,
    });
  };

  let y = drawChrome(false);

  // Client-facing engagement intro (replaces internal disclaimer)
  const orgLabel = (ctx?.clientOrgName ?? "").trim() || "the client";
  const intro =
    `An External Vulnerability Assessment and Penetration Test (VAPT) was conducted for ${orgLabel} as part of this engagement. The findings below summarize notable technical observations identified during testing. The complete VAPT report, including technical details, supporting evidence, and remediation guidance, will be delivered separately alongside this cybersecurity risk assessment report.`;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  const discLines = doc.splitTextToSize(intro, innerW);
  doc.text(discLines, PAGE_MARGIN, y, { lineHeightFactor: 1.5 } as any);
  y += discLines.length * 14 + 12;

  const narrative = buildTvfNarrative(rows);
  if (narrative) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    const nLines = doc.splitTextToSize(narrative, innerW);
    doc.text(nLines, PAGE_MARGIN, y, { lineHeightFactor: 1.5 } as any);
    y += nLines.length * 14 + 12;
  }

  const s = summarizeTvf(rows);
  const sevOrder = ["Critical", "High", "Medium", "Low", "Informational"];
  const sevText = sevOrder
    .filter((sv) => s.bySeverity[sv])
    .map((sv) => `${sv}: ${s.bySeverity[sv]}`)
    .join("   ·   ");
  if (sevText) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.text(sevText, PAGE_MARGIN, y);
    y += 16;
  }
  y += 6;

  // ── Table — Promotion column removed (internal-only) ──
  const cols = [
    { key: "severity",       label: "Severity",       weight: 0.13 },
    { key: "category",       label: "Category",       weight: 0.18 },
    { key: "affected_asset", label: "Affected Asset", weight: 0.24 },
    { key: "finding_title",  label: "Finding Title",  weight: 0.32 },
    { key: "status",         label: "Status",         weight: 0.13 },
  ] as const;
  const totalW = cols.reduce((a, c) => a + c.weight, 0);
  const colW = cols.map((c) => (c.weight / totalW) * innerW);
  const colX: number[] = [];
  {
    let cx = PAGE_MARGIN;
    for (let i = 0; i < colW.length; i++) { colX.push(cx); cx += colW[i]; }
  }

  const CELL_PAD_X = 7;
  const ROW_PAD_Y = 10;
  const LINE_H = 12;
  const BODY_FONT = 9.5;

  const wrapCell = (text: string, maxWidth: number): string[] =>
    doc.splitTextToSize(String(text ?? ""), maxWidth - CELL_PAD_X * 2) as string[];

  const drawHeader = (atY: number): number => {
    const h = 24;
    // Lighter accent-blue header (matches Vendor Exposure / Components tables)
    doc.setFillColor(...REPORT_THEME.accent.rgb);
    doc.rect(PAGE_MARGIN, atY, innerW, h, "F");
    doc.setFillColor(...REPORT_THEME.gold.rgb);
    doc.rect(PAGE_MARGIN, atY, 3, h, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(255, 255, 255);
    cols.forEach((c, i) => {
      doc.text(c.label, colX[i] + CELL_PAD_X, atY + 16);
    });
    return atY + h;
  };

  let page = ctx?.startPageNumber ?? doc.getNumberOfPages();
  y = drawHeader(y);

  // Friendly asset name: strip scheme/www, drop long URL paths.
  const friendlyAsset = (raw: string): string => {
    const s = String(raw ?? "").trim();
    if (!s) return "—";
    try {
      const u = new URL(s.includes("://") ? s : `https://${s}`);
      const host = u.hostname.replace(/^www\./i, "");
      // GitHub repo shorthand
      if (/github\.com$/i.test(host)) {
        const parts = u.pathname.split("/").filter(Boolean);
        if (parts.length >= 2) return `GitHub: ${parts[0]}/${parts[1]}`;
        return "GitHub Repository";
      }
      return host || s;
    } catch {
      // Not a URL — just trim overly long strings.
      return s.length > 60 ? s.slice(0, 57) + "…" : s;
    }
  };

  let zebra = false;
  for (const row of rows) {
    const cells = [
      String(row.severity || "—"),
      String(row.category || "—"),
      friendlyAsset(String(row.affected_asset || "—")),
      String(row.finding_title || "—"),
      String(row.status || "—"),
    ];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(BODY_FONT);
    const wrapped = cells.map((c, i) => wrapCell(c, colW[i]));
    const rowH = Math.max(...wrapped.map((w) => w.length)) * LINE_H + ROW_PAD_Y * 2;

    if (y + rowH > bottomLimit) {
      drawPageFooter(doc, page);
      page += 1;
      doc.addPage();
      y = drawChrome(true);
      y = drawHeader(y);
      zebra = false;
    }

    if (zebra) {
      doc.setFillColor(247, 249, 252);
      // Inset 1pt top & bottom so adjacent row dividers remain visible
      // (otherwise the next row's fill paints over the previous bottom line,
      // producing the "missing horizontal border" artifact on every page).
      doc.rect(PAGE_MARGIN, y + 1, innerW, rowH - 2, "F");
    }
    doc.setDrawColor(...REPORT_THEME.border.rgb);
    doc.setLineWidth(0.5);
    doc.line(PAGE_MARGIN, y + rowH, PAGE_MARGIN + innerW, y + rowH);
    // Also draw the TOP divider for this row so the line survives any
    // overlap with the prior row's fill or page-break chrome.
    doc.line(PAGE_MARGIN, y, PAGE_MARGIN + innerW, y);

    wrapped.forEach((lines, i) => {
      const key = cols[i].key;
      const baseY = y + ROW_PAD_Y + LINE_H - 2;
      if (key === "severity") {
        const sevLabel = cells[i];
        const fill = SEVERITY_COLORS[sevLabel] ?? (REPORT_THEME.textMuted.rgb as RGB);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        const tw = doc.getTextWidth(sevLabel);
        const bw = tw + 12;
        const bh = 15;
        doc.setFillColor(...fill);
        doc.roundedRect(colX[i] + CELL_PAD_X, y + (rowH - bh) / 2, bw, bh, 2, 2, "F");
        doc.setTextColor(255, 255, 255);
        doc.text(sevLabel, colX[i] + CELL_PAD_X + 6, y + rowH / 2 + 3);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(BODY_FONT);
        doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
      } else if (key === "finding_title") {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...REPORT_THEME.navy.rgb);
        doc.text(lines, colX[i] + CELL_PAD_X, baseY, { lineHeightFactor: 1.3 } as any);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
      } else {
        doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
        doc.text(lines, colX[i] + CELL_PAD_X, baseY, { lineHeightFactor: 1.3 } as any);
      }
    });
    y += rowH;
    zebra = !zebra;
  }

  drawPageFooter(doc, page);
  return page;
}
